import React, { useEffect, useState } from 'react'
import { Form, Button, Row, Col } from "react-bootstrap";
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/Portada.css'
import { useDarkMode } from '../utils/DarkModeProvider';
import { ScaleLoader } from 'react-spinners';
import Swal from 'sweetalert2';

export default function Portada({ token, portadaDocente, loading, periodoId }) {

    const { darkMode } = useDarkMode();
    const [tituloMaximo, setTituloMaximo] = useState('');
    const [anioObtencion, setAnioObtencion] = useState('');
    const [institucionOtorgante, setInstitucionOtorgante] = useState('');
    const [telefono, setTelefono] = useState('');
    const [correoInstitucional, setCorreoInstitucional] = useState('');
    const [correoAlternativo, setCorreoAlternativo] = useState('');
    const [documentos, setDocumentos] = useState([]);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });
    const [subiendoDocumento, setSubiendoDocumento] = useState(false);
    const [eliminandoDocumentoId, setEliminandoDocumentoId] = useState(null);

    const dynamicStyles = darkMode ? darkModeStyles : lightModeStyles;

    useEffect(() => {
        if (portadaDocente) {
            setTituloMaximo(portadaDocente.titulo_maximo_obtenido || '');
            setAnioObtencion(portadaDocente.anio_de_obtencion || '');
            setInstitucionOtorgante(portadaDocente.institucion_otorgante || '');
            setTelefono(portadaDocente.numero_telefono || '');
            setCorreoInstitucional(portadaDocente.correo_electronico_institucional || '');
            setCorreoAlternativo(portadaDocente.correo_electronico_alternativo || '');
            setDocumentos(portadaDocente.documentos || []);
        }
    }, [portadaDocente]);

    const handleGuardar = async () => {
        if (!portadaDocente?.docente || !periodoId) {
            setMessage({ text: 'No se pudo identificar el docente', type: 'error' });
            return;
        }

        const anioNormalizado = String(anioObtencion || '').trim()
        if (anioNormalizado !== '' && !/^\d{4}$/.test(anioNormalizado)) {
            setMessage({ text: 'El Año de Obtención debe contener exactamente 4 números', type: 'error' })
            return
        }

        const anioParaEnviar = anioNormalizado === '' ? null : parseInt(anioNormalizado, 10)

        setSaving(true);
        setMessage({ text: '', type: '' });

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/docente-portada/actualizar/${portadaDocente.docente}/${periodoId}/`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    titulo_maximo_obtenido: tituloMaximo,
                    anio_de_obtencion: anioParaEnviar,
                    institucion_otorgante: institucionOtorgante,
                    numero_telefono: telefono,
                    correo_electronico_institucional: correoInstitucional,
                    correo_electronico_alternativo: correoAlternativo
                })
            });

            if (response.ok) {
                setMessage({ text: '¡Datos guardados exitosamente!', type: 'success' });
                setTimeout(() => setMessage({ text: '', type: '' }), 3000);
            } else {
                const errorData = await response.json();
                console.log('errorData: ', errorData);
                setMessage({ text: errorData.msg || 'Error al guardar los datos', type: 'error' });
            }
        } catch (error) {
            setMessage({ text: 'Error de conexión al guardar los datos', type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const descargarDocumento = async (idDocumento, descripcion) => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/docente-portada/${portadaDocente.docente}/${periodoId}/documentos/${idDocumento}/descargar/`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            )

            if (!response.ok) {
                throw new Error('Error al descargar el documento')
            }

            const blob = await response.blob()
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = descripcion || 'documento'
            document.body.appendChild(a)
            a.click()
            window.URL.revokeObjectURL(url)
            document.body.removeChild(a)
        } catch (error) {
            console.log(error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudo descargar el documento',
                confirmButtonText: 'Cerrar',
                confirmButtonColor: '#972034'
            })
        }
    }

    const subirDocumento = async (event) => {
        const file = event.target.files?.[0]
        if (!file || !portadaDocente?.docente || !periodoId) return

        const confirmacion = await Swal.fire({
            icon: 'question',
            title: 'Confirmar subida',
            text: `¿Está seguro que desea subir el documento "${file.name}"?`,
            showCancelButton: true,
            confirmButtonText: 'Aceptar',
            cancelButtonText: 'Cancelar'
        })

        if (!confirmacion.isConfirmed) {
            event.target.value = ''
            return
        }

        setSubiendoDocumento(true)
        try {
            const formData = new FormData()
            formData.append('file', file)

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/docente-portada/${portadaDocente.docente}/${periodoId}/documentos/`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    },
                    body: formData
                }
            )

            if (!response.ok) {
                throw new Error('Error al subir el documento')
            }

            const nuevoDoc = await response.json()
            setDocumentos([...documentos, nuevoDoc])

            Swal.fire({
                icon: 'success',
                title: 'Documento subido',
                text: 'El documento se subió correctamente',
                timer: 1500,
                showConfirmButton: false
            })

            event.target.value = ''
        } catch (error) {
            console.log(error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudo subir el documento',
                confirmButtonText: 'Cerrar',
                confirmButtonColor: '#972034'
            })
        } finally {
            setSubiendoDocumento(false)
        }
    }

    const eliminarDocumento = async (idDocumento, descripcion) => {
        if (!portadaDocente?.docente || !periodoId) return

        const confirmacion = await Swal.fire({
            icon: 'warning',
            title: 'Confirmar eliminación',
            text: `¿Está seguro que desea eliminar el documento "${descripcion || 'sin nombre'}"?`,
            showCancelButton: true,
            confirmButtonText: 'Aceptar',
            cancelButtonText: 'Cancelar'
        })

        if (!confirmacion.isConfirmed) return

        setEliminandoDocumentoId(idDocumento)
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/docente-portada/${portadaDocente.docente}/${periodoId}/documentos/${idDocumento}/eliminar/`,
                {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            )

            if (!response.ok) {
                throw new Error('Error al eliminar el documento')
            }

            setDocumentos((prevDocumentos) => prevDocumentos.filter((doc) => doc.id !== idDocumento))

            Swal.fire({
                icon: 'success',
                title: 'Documento eliminado',
                text: 'El documento se eliminó correctamente',
                timer: 1500,
                showConfirmButton: false
            })
        } catch (error) {
            console.log(error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudo eliminar el documento',
                confirmButtonText: 'Cerrar',
                confirmButtonColor: '#972034'
            })
        } finally {
            setEliminandoDocumentoId(null)
        }
    }

    return (
        <div className="mb-3">
            {loading ?
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '200px'
                }}>
                    <ScaleLoader color='#972034' />
                </div>
                :
                <>
                    <h2 className="pb-1" style={{ ...dynamicStyles.title, marginBottom: 10, fontSize: '1.45rem' }}>Actualización de formación académica y de datos de contacto</h2>
                    {portadaDocente &&
                        <div style={styles.formContainer}>
                            <Form>
                                <Form.Group className="mb-2">
                                    <Form.Label style={styles.label}>Título Máximo Obtenido</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={tituloMaximo}
                                        onChange={(e) => setTituloMaximo(e.target.value)}
                                        className={`portada-input ${darkMode ? 'portada-input-dark' : 'portada-input-light'}`}
                                        style={{ ...styles.input, ...(darkMode ? styles.inputDark : styles.inputLight) }}
                                        placeholder="Licenciatura..."
                                    />
                                </Form.Group>

                                <Form.Group className="mb-2">
                                    <Form.Label style={styles.label}>Año de Obtención</Form.Label>
                                    <Form.Control
                                        type="text"
                                        maxLength={4}
                                        inputMode="numeric"
                                        pattern="[0-9]{4}"
                                        value={anioObtencion}
                                        onChange={(e) => {
                                            const soloNumeros = e.target.value.replace(/\D/g, '').slice(0, 4)
                                            setAnioObtencion(soloNumeros)
                                        }}
                                        className={`portada-input ${darkMode ? 'portada-input-dark' : 'portada-input-light'}`}
                                        style={{ ...styles.input, ...(darkMode ? styles.inputDark : styles.inputLight) }}
                                        placeholder="2023"
                                    />
                                </Form.Group>

                                <Form.Group className="mb-2">
                                    <Form.Label style={styles.label}>Institución Otorgante</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={institucionOtorgante}
                                        onChange={(e) => setInstitucionOtorgante(e.target.value)}
                                        className={`portada-input ${darkMode ? 'portada-input-dark' : 'portada-input-light'}`}
                                        style={{ ...styles.input, ...(darkMode ? styles.inputDark : styles.inputLight) }}
                                        placeholder="Universidad Nacional de Quilmes"
                                    />
                                </Form.Group>

                                <Form.Group className="mb-2">
                                    <Form.Label style={styles.label}>Teléfono celular</Form.Label>
                                    <Form.Control
                                        type="text"
                                        maxLength={40}
                                        inputMode="tel"
                                        pattern="[0-9-]*"
                                        value={telefono}
                                        onChange={(e) => {
                                            const soloNumerosYGuion = e.target.value.replace(/[^0-9-]/g, '');
                                            setTelefono(soloNumerosYGuion);
                                        }}
                                        className={`portada-input ${darkMode ? 'portada-input-dark' : 'portada-input-light'}`}
                                        style={{ ...styles.input, ...(darkMode ? styles.inputDark : styles.inputLight) }}
                                        placeholder="Ingrese teléfono"
                                    />
                                </Form.Group>

                                {/* <Form.Group className="mb-2">
                                    <Form.Label style={styles.label}>Correo electrónico institucional</Form.Label>
                                    <Form.Control
                                        type="email"
                                        value={correoInstitucional}
                                        onChange={(e) => setCorreoInstitucional(e.target.value)}
                                        className={`portada-input ${darkMode ? 'portada-input-dark' : 'portada-input-light'}`}
                                        style={{...styles.input, ...(darkMode ? styles.inputDark : styles.inputLight)}}
                                        placeholder="usuario@unq.edu.ar"
                                    />
                                </Form.Group> */}

                                <Form.Group className="mb-2">
                                    <Form.Label style={styles.label}>Correo electrónico alternativo</Form.Label>
                                    <Form.Control
                                        type="email"
                                        value={correoAlternativo}
                                        onChange={(e) => setCorreoAlternativo(e.target.value)}
                                        className={`portada-input ${darkMode ? 'portada-input-dark' : 'portada-input-light'}`}
                                        style={{ ...styles.input, ...(darkMode ? styles.inputDark : styles.inputLight) }}
                                        placeholder="usuario@gmail.com"
                                    />
                                </Form.Group>

                                {message.text && (
                                    <div style={{
                                        ...styles.message,
                                        ...(message.type === 'success' ? styles.messageSuccess : styles.messageError)
                                    }}>
                                        {message.text}
                                    </div>
                                )}

                                <Button
                                    onClick={handleGuardar}
                                    disabled={saving}
                                    style={{
                                        ...styles.button,
                                        opacity: saving ? 0.7 : 1,
                                        cursor: saving ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {saving ? 'Guardando...' : 'Guardar'}
                                </Button>
                            </Form>

                            <div style={styles.divider}></div>

                            <div style={styles.section}>
                                <h3 style={{ ...dynamicStyles.title, marginBottom: 8, paddingBottom: 4, fontSize: '1.2rem' }}>Documentos</h3>
                                {documentos && documentos.length > 0 ? (
                                    <ul style={styles.list}>
                                        {documentos.map((doc) => (
                                            <li key={doc.id} style={styles.listItem}>
                                                <div style={styles.documentoRow}>
                                                    <button
                                                        onClick={() => descargarDocumento(doc.id, doc.descripcion)}
                                                        style={styles.linkButton}
                                                    >
                                                        📄 {doc.descripcion}
                                                    </button>
                                                    <button
                                                        onClick={() => eliminarDocumento(doc.id, doc.descripcion)}
                                                        disabled={eliminandoDocumentoId === doc.id}
                                                        style={{
                                                            ...styles.deleteButton,
                                                            opacity: eliminandoDocumentoId === doc.id ? 0.6 : 1,
                                                            cursor: eliminandoDocumentoId === doc.id ? 'not-allowed' : 'pointer'
                                                        }}
                                                        title="Eliminar documento"
                                                        aria-label={`Eliminar documento ${doc.descripcion || ''}`}
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p style={{ fontStyle: 'italic', color: '#666', marginTop: 8 }}>No hay documentos cargados</p>
                                )}

                                <div style={{ marginTop: 8 }}>
                                    <input
                                        type="file"
                                        id="fileInputDocumentos"
                                        onChange={subirDocumento}
                                        disabled={subiendoDocumento}
                                        style={{ display: 'none' }}
                                    />
                                    <button
                                        onClick={() => document.getElementById('fileInputDocumentos').click()}
                                        disabled={subiendoDocumento}
                                        style={{ ...styles.button, opacity: subiendoDocumento ? 0.6 : 1 }}
                                    >
                                        {subiendoDocumento ? 'Subiendo...' : '📁 Subir Documento'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    }
                </>
            }
        </div>
    )
}

const lightModeStyles = {
    title: {
        color: '#A23C41',
        borderBottom: '4px solid #646354'
    }
};

const darkModeStyles = {
    title: {
        borderBottom: '4px solid #495057'
    }
};

const styles = {
    formContainer: {
        maxWidth: '600px',
        padding: '14px',
        borderRadius: '10px',
        backgroundColor: 'transparent'
    },
    label: {
        fontWeight: '600',
        fontSize: '15px',
        marginBottom: '6px',
        color: '#333'
    },
    input: {
        borderRadius: '8px',
        padding: '9px 12px',
        fontSize: '14px',
        border: '2px solid #e0e0e0',
        transition: 'all 0.3s ease',
        outline: 'none'
    },
    inputLight: {
        backgroundColor: '#fff',
        color: '#333'
    },
    inputDark: {
        backgroundColor: '#2d2d2d',
        color: '#fff',
        border: '2px solid #495057'
    },
    button: {
        backgroundColor: '#972034',
        marginTop: '8px',
        color: '#fff',
        padding: '9px 22px',
        border: 'none',
        borderRadius: '8px',
        fontSize: '15px',
        fontWeight: '600',
        transition: 'all 0.3s ease',
        boxShadow: '0 4px 6px rgba(151, 32, 52, 0.2)'
    },
    linkButton: {
        background: 'none',
        border: 'none',
        color: '#007bff',
        cursor: 'pointer',
        fontSize: '13px',
        textDecoration: 'underline',
        padding: 0,
        transition: 'color 0.3s'
    },
    message: {
        padding: '8px 12px',
        borderRadius: '8px',
        marginBottom: '10px',
        fontSize: '14px',
        fontWeight: '500'
    },
    messageSuccess: {
        backgroundColor: '#d4edda',
        color: '#155724',
        border: '1px solid #c3e6cb'
    },
    messageError: {
        backgroundColor: '#f8d7da',
        color: '#721c24',
        border: '1px solid #f5c6cb'
    },
    divider: {
        height: '1px',
        backgroundColor: '#ddd',
        margin: '16px 0'
    },
    section: {
        marginTop: '10px'
    },
    list: {
        paddingLeft: '16px',
        listStyleType: 'disc',
        marginTop: '8px'
    },
    listItem: {
        marginBottom: '4px'
    },
    documentoRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px'
    },
    deleteButton: {
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        border: 'none',
        backgroundColor: '#dc3545',
        color: '#fff',
        fontSize: '16px',
        fontWeight: 'bold',
        lineHeight: '24px',
        padding: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center'
    }
};